import { SECOND } from '@gorila-shared-ui/components';
import Hls from 'hls.js';
import { useEffect, useState } from 'react';
import useUpdateEffect from '../../../hooks/useUpdateEffect';

type Props = {
  initialVolume: number;
  playerRef: React.RefObject<HTMLVideoElement>;
  src: string;
  internalError?: boolean;
  pauseOnPlay?: boolean;
  onBadRequest: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onLoadEnd?: () => void;
};

function HlsPlayer({
  initialVolume,
  internalError = false,
  muted = false,
  playerRef,
  src,
  pauseOnPlay,
  onBadRequest,
  onLoadEnd,
  onPause,
  onPlay,
  ...props
}: Readonly<Props & Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'controlsList'>>) {
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const volumeTimeout = setTimeout(() => {
      if (playerRef.current) {
        playerRef.current.volume = initialVolume;
      }
    }, 0);
    return () => {
      clearTimeout(volumeTimeout);
    };
  }, [playerRef]);

  useUpdateEffect(() => {
    playerRef.current?.pause();
  }, [internalError]);

  useEffect(() => {
    if (!pauseOnPlay) return;
    const timeout = setTimeout(() => {
      playerRef.current?.pause();
    }, SECOND * 15);
    return () => {
      clearTimeout(timeout);
    };
  }, [playerRef, playing, pauseOnPlay]);

  useEffect(() => {
    if (!playerRef.current) return;

    const video = playerRef.current;

    if (Hls.isSupported()) {
      const hls = new Hls({
        autoStartLoad: false, // 🚫 don't start automatically
        xhrSetup: (xhr) => {
          xhr.onloadend = (request) => {
            const currentTarget = request.currentTarget as XMLHttpRequest;
            if (currentTarget.status >= 400) {
              video.pause();
              onBadRequest();
            }
            onLoadEnd?.();
          };
        },
      });

      hls.loadSource(src);
      hls.attachMedia(video);

      const handlePlay = () => {
        // Resume downloading fragments when playback starts
        hls.startLoad();
        onPlay?.();
        setPlaying(true);
      };

      const handlePause = () => {
        // Stop downloading fragments to save bandwidth
        hls.stopLoad();
        onPause?.();
        setPlaying(false);
      };

      // --- Handle HLS-level errors and disconnections ---
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) {
          if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
            const video = playerRef.current;
            if (!video) return;
            // Try to resume if the video is paused due to stall
            if (video.paused) {
              video.play().catch(() => {});
            }
            // Restart fragment loading
            hls.startLoad();
          }
        }
      });

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        hls.destroy();
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Note: Native support (e.g., Safari) — you *cannot* set headers here
      // Safari - no header control or stopLoad/startLoad support
      video.src = src;
    }
  }, [src]);

  return (
    <video
      {...props}
      ref={playerRef}
      controlsList="nofullscreen nodownload"
      preload="none"
    />
  );
}

export default HlsPlayer;
